import {Component, EventEmitter, HostListener, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatTooltipModule} from '@angular/material/tooltip';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectInputComponent} from '../../forms/select-input/select-input.component';
import {SlideToggleInputComponent} from '../../forms/slide-select/slide-toggle-input.component';
import {OpenaiService} from '../../../api/services/openai.service';
import {MapService} from '../../../api/services/map.service';
import {CytoscapeService} from '../../../services/cy-services/cytoscape.service';
import {DashboardComponent} from '../../../dashboard/dashboard.component';
import {SettingsDialogComponent} from '../../../dialogs/settings-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {NotificationService} from '../../../services/notification.service';
import {CreditApiService} from '../../../api/services/credit.service';
import {GenerationStateService} from '../../../services/generation-state.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ImageWarningPreferenceService} from './image-warning-preference.service';
import {TrialStateService} from '../../../services/trial-state.service';
import {EndTrialFrameComponent} from '../../../dialogs/end-trial-frame.component';

export interface AIGenerationOptions {
  description: 'maintain' | 'refresh';
  properties: 'maintain' | 'refresh' | 'complete' | 'extend' | 'replace';
  longPressOnly: boolean;
}

export interface AIGenerationBubbleConfig {
  // MAP-1583: 'image-warning' is a confirmation gate shown before an AI image
  // lookup runs — same bubble shell, a red experimental notice, a GENERATE
  // button and a "do not show this again" checkbox. The generation itself is
  // run by the host (it owns the target field), so this type only emits.
  type: 'configure' | 'generate' | 'image-warning';
  // MAP-1607: during the free trial the image lookup is unavailable, so the
  // warning bubble is replaced by the trial frame (no GENERATE, no checkbox).
  trialLocked?: boolean;
  hasDescription?: boolean;
  hasProperties?: boolean;
  hasEmptyProperties?: boolean;
  forMap?: boolean;
  // MAP-1583 (review round 3): the sidebar warning has to name what the lookup
  // targets, so the user cannot read it as "GENERATE will replace ALL images".
  imageWarningTarget?: 'smap' | 'node';
}

/**
 * MAP-1583 (review round 3): the sidebar bubble's wording differs from the
 * smap-generation dialog's. The shared sentence lives in one parametric key and
 * only the trailing clause varies per object type.
 */
export const IMAGE_WARNING_TARGET_KEYS: Record<'smap' | 'node', string> = {
  smap: 'AI_ASSISTANT.IMAGE_WARNING_TARGET_SMAP',
  node: 'AI_ASSISTANT.IMAGE_WARNING_TARGET_NODE',
};

@Component({
  selector: 'app-ai-generation-bubble',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    ReactiveFormsModule,
    SelectInputComponent,
    SlideToggleInputComponent,
    TranslatePipe,
    EndTrialFrameComponent,
  ],
  templateUrl: './ai-generation-bubble.component.html',
  styleUrl: './ai-generation-bubble.component.scss'
})
export class AiGenerationBubbleComponent implements OnInit {
  @Input() config!: AIGenerationBubbleConfig;
  @Input() initialOptions?: Partial<AIGenerationOptions>;

  @Output() openSettings = new EventEmitter<void>();
  @Output() generate = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();
  @Output() longPressToggle = new EventEmitter<boolean>();

  form!: FormGroup;
  isSubmitting = false;
  doNotShowAgain = false;
  imageWarningText = '';

  descriptionOptions: { key: string; value: string; icon: string; tooltipText: string }[] = [];

  propertiesOptions: { key: string; value: string; icon: string; tooltipText: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private openaiService: OpenaiService,
    private mapService: MapService,
    private matDilog: MatDialog,
    private cytoscapeService: CytoscapeService,
    private notificationService: NotificationService,
    private creditApiService: CreditApiService,
    private generationStateService: GenerationStateService,
    private translate: TranslateService,
    private imageWarningPreference: ImageWarningPreferenceService,
    private trialStateService: TrialStateService
  ) {}

  ngOnInit() {
    this.buildOptions();
    this.initializeForm();
    if (this.isImageWarning) {
      this.doNotShowAgain = this.imageWarningPreference.isDismissed();
      this.imageWarningText = this.buildImageWarningText();
    }
  }

  private buildImageWarningText(): string {
    const target = this.config.imageWarningTarget ?? 'smap';
    return this.translate.instant('AI_ASSISTANT.IMAGE_EXPERIMENTAL_WARNING_FOR_TARGET', {
      target: this.translate.instant(IMAGE_WARNING_TARGET_KEYS[target]),
    });
  }

  private buildOptions() {
    this.descriptionOptions = [
      { key: 'maintain', value: this.translate.instant('AI_ASSISTANT.OPT_MAINTAIN'), icon: 'drag_handle', tooltipText: this.translate.instant('AI_ASSISTANT.MAINTAIN_DESC_TOOLTIP') },
      { key: 'refresh', value: this.translate.instant('AI_ASSISTANT.OPT_REFRESH'), icon: 'refresh', tooltipText: this.translate.instant('AI_ASSISTANT.REFRESH_DESC_TOOLTIP') }
    ];

    this.propertiesOptions = [
      { key: 'maintain', value: this.translate.instant('AI_ASSISTANT.OPT_MAINTAIN'), icon: 'drag_handle', tooltipText: this.translate.instant('AI_ASSISTANT.MAINTAIN_PROPS_TOOLTIP') },
      { key: 'refresh', value: this.translate.instant('AI_ASSISTANT.OPT_REFRESH'), icon: 'refresh', tooltipText: this.translate.instant('AI_ASSISTANT.REFRESH_PROPS_TOOLTIP') },
      { key: 'complete', value: this.translate.instant('AI_ASSISTANT.OPT_COMPLETE'), icon: 'playlist_add_check', tooltipText: this.translate.instant('AI_ASSISTANT.COMPLETE_PROPS_TOOLTIP') },
      { key: 'extend', value: this.translate.instant('AI_ASSISTANT.OPT_EXTEND'), icon: 'playlist_add', tooltipText: this.translate.instant('AI_ASSISTANT.EXTEND_PROPS_TOOLTIP') },
      { key: 'replace', value: this.translate.instant('AI_ASSISTANT.OPT_REPLACE'), icon: 'cached', tooltipText: this.translate.instant('AI_ASSISTANT.REPLACE_PROPS_TOOLTIP') }
    ];
  }

  private initializeForm() {
    const defaultOptions = this.getDefaultOptions();

    this.form = this.fb.group({
      description: [this.initialOptions?.description || defaultOptions.description],
      properties: [this.initialOptions?.properties || defaultOptions.properties],
      longPressOnly: [this.getCurrentLongPressSettingFromStorage()]
    });

    // Watch for long press toggle changes
    this.form.get('longPressOnly')?.valueChanges.subscribe(value => {
      this.longPressToggle.emit(value);
    });
  }

  private getCurrentLongPressSettingFromStorage(): boolean {
    const settings = localStorage.getItem('app-settings');
    if (settings) {
      const parsed = JSON.parse(settings);
      return parsed.aiGenerationLongPressOnly || false;
    }
    return false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isSubmitting) return;
    setTimeout(() => {
      const target = event.target as HTMLElement;
      const bubbleElement = target.closest('.ai-bubble');
      const generateButton = target.closest('[data-generate-button]');

      const dropdownElement = target.closest('.custom-dropdown') ||
                            target.closest('.dropdown-menu') ||
                            target.closest('.mat-select-panel') ||
                            target.closest('.mat-mdc-menu-panel') ||
                            target.closest('.cdk-overlay-backdrop') ||
                            target.closest('mat-option') ||
                            target.closest('.cdk-overlay-pane');

      if (!bubbleElement && !generateButton && !dropdownElement) {
        this.close.emit();
      }
    }, 10);
  }

  private getDefaultOptions(): AIGenerationOptions {
    // Dynamic initial selection logic based on current element state
    const description = this.config.hasDescription ? 'maintain' : 'refresh';

    let properties: AIGenerationOptions['properties'];
    if (this.config.hasEmptyProperties) {
      properties = 'complete';
    } else if (!this.config.hasProperties || (this.config.hasProperties && !this.config.hasEmptyProperties)) {
      properties = 'extend';
    } else {
      properties = 'maintain';
    }

    return {
      description: description as 'maintain' | 'refresh',
      properties,
      longPressOnly: false
    };
  }

  onOpenSettings() {
    this.matDilog.open(SettingsDialogComponent, {
      width: '35%',
      data: {
        focusApiKey: true
      }
    });
    this.close.emit();
  }

  onGenerate() {
    if (this.isSubmitting) return;
    this.creditApiService.ensureCredits(this.matDilog, 'properties').subscribe(ok => {
      if (!ok) return;
      this.isSubmitting = true;

      const options: AIGenerationOptions = {
        description: this.form.get('description')?.value,
        properties: this.form.get('properties')?.value,
        longPressOnly: this.form.get('longPressOnly')?.value
      };
      const elementId = this.config.forMap ? null : (DashboardComponent?.tappedNode?.id || DashboardComponent?.tappedLink?.id);
      let metaData;
      let dto: any;

      if (elementId) {
        const data = this.cytoscapeService.cy?.$("#" + elementId).data();
        metaData = data?.metadata || [];
        dto = {
          title: data?.name || '',
          description: data?.description || '',
          metadata: metaData,
        }
      } else {
        const selectedMap = this.mapService.getCurrentSelectedMapFromStore();
        metaData = selectedMap?.metadata || [];
        dto = {
          title: selectedMap?.name || '',
          description: selectedMap?.description || '',
          metadata: metaData,
        };
      }

      // Call the API directly with options
      const source = this.config.forMap ? 'map' as const : (DashboardComponent?.tappedLink ? 'link' as const : 'node' as const);
      // Drives the property-generation icon animation while the request runs
      // (MAP-1402). One id per call — the source + element id + timestamp is unique enough.
      const taskId = `${source}:${elementId ?? 'map'}:${Date.now()}`;
      this.generationStateService.startPropertyGeneration(taskId);
      this.openaiService.extendMetadata(dto, this.mapService.getCurrentSelectedMapFromStore().id, options, source)
        .subscribe({
          next: (result) => {
            this.generationStateService.endPropertyGeneration(taskId);
            this.isSubmitting = false;
            this.generate.emit();
            this.close.emit();
          },
          error: (error) => {
            this.generationStateService.endPropertyGeneration(taskId);
            this.isSubmitting = false;
            const errorMessage = error.error?.message || error.message || this.translate.instant('AI_ASSISTANT.GENERATE_PROPS_ERROR');
            this.notificationService.error(errorMessage, {
              title: this.translate.instant('AI_ASSISTANT.AI_GENERATION_FAILED_TITLE'),
              duration: 0 // Persistent until dismissed
            });
            this.close.emit();
          }
        });
    });
  }


  get isGenerateDisabled(): boolean {
    const description = this.form.get('description')?.value;
    const properties = this.form.get('properties')?.value;
    return description === 'maintain' && properties === 'maintain';
  }

  get isConfigure(): boolean {
    return this.config.type === 'configure';
  }

  get isGenerate(): boolean {
    return this.config.type === 'generate';
  }

  get isImageWarning(): boolean {
    return this.config.type === 'image-warning';
  }

  get isTrialLocked(): boolean {
    return !!this.config.trialLocked && this.trialStateService.isOnTrial;
  }

  /** Ending the trial swaps this bubble back to the regular image warning. */
  onImageTrialEnded(): void {
    this.trialStateService.markTrialEnded();
  }

  /**
   * MAP-1583 (review round 3): persisted the moment the box is ticked, not on
   * GENERATE. Closing the bubble by ANY route (outside click, Escape, the
   * trigger toggling it off, the overlay detaching on scroll or destroy) then
   * keeps the choice, because there is no pending state left to flush.
   * Unticking clears it again, so the two directions stay symmetric.
   */
  onDoNotShowAgainChange(checked: boolean) {
    this.doNotShowAgain = checked;
    this.imageWarningPreference.setDismissed(checked);
  }

  /**
   * MAP-1583: the host runs the image lookup and closes the bubble when the
   * URL arrives, so this only flips the button into its GENERATING… state.
   */
  onImageWarningGenerate() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.generate.emit();
  }
}
